import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import en from "../i18n/en.json";
import es from "../i18n/es.json";

type Locale = "en" | "es";
type Messages = Record<string, string>;

type IntlCtx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const ALL: Record<Locale, Messages> = { en, es };

const Ctx = createContext<IntlCtx | null>(null);

export function IntlProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = localStorage.getItem("juris.locale");
    if (stored === "en" || stored === "es") return stored;
    return navigator.language.startsWith("es") ? "es" : "en";
  });

  const t = useMemo(() => {
    return (key: string, vars?: Record<string, string | number>) => {
      let v = ALL[locale][key] ?? ALL.en[key] ?? key;
      if (vars) {
        for (const [k, val] of Object.entries(vars)) {
          v = v.replace(`{${k}}`, String(val));
        }
      }
      return v;
    };
  }, [locale]);

  const persist = (l: Locale) => {
    localStorage.setItem("juris.locale", l);
    setLocale(l);
  };

  const value = useMemo(() => ({ locale, setLocale: persist, t }), [locale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIntl(): IntlCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useIntl: IntlProvider missing");
  return c;
}
